import { useForm } from "react-hook-form";

import { zodResolver } from "@hookform/resolvers/zod";

import { BottomActionBar } from "@/components/bottom-action-bar";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { GroupSitePicker } from "@/pages/tina/groups/components/group-site-picker";
import { TimeOfDayField } from "@/pages/tina/groups/components/time-of-day-field";
import { WeekdayPicker } from "@/pages/tina/groups/components/weekday-picker";
import {
  type GroupFormType,
  groupFormSchema,
} from "@/pages/tina/groups/schemas/group.form";

export interface GroupFormProps {
  defaultValues: GroupFormType;
  submitLabel: string;
  onSubmit: (values: GroupFormType) => Promise<void>;
}

export function GroupForm({
  defaultValues,
  submitLabel,
  onSubmit,
}: GroupFormProps) {
  const form = useForm<GroupFormType>({
    resolver: zodResolver(groupFormSchema),
    defaultValues,
  });

  return (
    <Form {...form}>
      {/* pb-24 keeps the last field clear of the fixed BottomActionBar. */}
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="flex flex-col gap-6 pb-24"
      >
        {/* Short fields stay in a readable column; the site picker below spans full width. */}
        <div className="flex max-w-xl flex-col gap-6">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Name</FormLabel>
                <FormControl>
                  <Input placeholder="e.g. greece-daily" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="time_of_day"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Time of day</FormLabel>
                <FormControl>
                  <TimeOfDayField
                    value={field.value}
                    onChange={field.onChange}
                  />
                </FormControl>
                <FormDescription>
                  24-hour, in Europe/Athens (ops timezone).
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="days_of_week"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Weekdays</FormLabel>
                <FormControl>
                  <WeekdayPicker
                    value={field.value}
                    onChange={field.onChange}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="sites"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Sites</FormLabel>
              <FormControl>
                <GroupSitePicker
                  selected={field.value}
                  onChange={field.onChange}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="enabled"
          render={({ field }) => (
            <FormItem className="flex max-w-xl items-center gap-3">
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
              <FormLabel className="!mt-0">Enabled</FormLabel>
            </FormItem>
          )}
        />

        <BottomActionBar className="justify-end">
          <Button type="submit" disabled={form.formState.isSubmitting}>
            {submitLabel}
          </Button>
        </BottomActionBar>
      </form>
    </Form>
  );
}
